import React, { useEffect } from 'react';
import {
  Box, Container, Typography, Stack, Chip, Button, Divider,
  Alert, CircularProgress, Paper, Grid,
} from '@mui/material';
import { ArrowBack, OpenInNew, Timer, Star, Route, School } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCatalog } from '../../context/CatalogContext';

function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').trim();
}

export default function LearningPathDetailPage() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const { learningPaths, modules, loadLearningPaths, loadModules, loading } = useCatalog();

  useEffect(() => {
    if (learningPaths.length === 0) loadLearningPaths();
    if (modules.length === 0) loadModules();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const uid = decodeURIComponent(pathId || '');
  const path = learningPaths.find((p) => p.uid === uid);

  if (loading && learningPaths.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 12 }}><CircularProgress /></Box>;
  }
  if (!path) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error">Learning path not found. <Button onClick={() => navigate('/learning-paths')}>Browse all</Button></Alert>
      </Container>
    );
  }

  const pathModules = (path.modules || [])
    .map((uid) => modules.find((m) => m.uid === uid))
    .filter(Boolean);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 3 }}>Back</Button>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Learning Path" size="small" icon={<Route sx={{ fontSize: '14px !important' }} />}
              sx={{ backgroundColor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 }} />
            {(path.levels || []).map((l) => (
              <Chip key={l} label={l.charAt(0).toUpperCase() + l.slice(1)} size="small"
                sx={{ backgroundColor: '#f3f4f6', color: '#374151', fontWeight: 600 }} />
            ))}
          </Stack>

          <Typography variant="h3" fontWeight={800} letterSpacing="-1.5px"
            sx={{ mb: 2, lineHeight: 1.15, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            {path.title}
          </Typography>

          <Stack direction="row" spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
            {path.duration_in_minutes && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Timer sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {Math.round(path.duration_in_minutes / 60)}h total
                </Typography>
              </Stack>
            )}
            {path.number_of_children && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <School sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{path.number_of_children} modules</Typography>
              </Stack>
            )}
            {path.rating?.average && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Star sx={{ fontSize: 16, color: '#f59e0b' }} />
                <Typography variant="body2">{path.rating.average.toFixed(1)}</Typography>
              </Stack>
            )}
          </Stack>

          <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#374151', mb: 4, fontSize: '1.05rem' }}>
            {stripHtml(path.summary)}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Module list */}
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Modules in this path ({path.modules?.length || 0})
          </Typography>
          <Stack spacing={1.5}>
            {pathModules.length > 0
              ? pathModules.map((m, i) => (
                <Paper
                  key={m.uid}
                  sx={{
                    p: 2, borderRadius: '10px', border: '1px solid #e5e7eb',
                    cursor: 'pointer', transition: 'box-shadow 0.15s',
                    '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
                  }}
                  onClick={() => navigate(`/modules/${encodeURIComponent(m.uid)}`)}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '8px',
                      backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Typography variant="caption" fontWeight={700} color="primary">{i + 1}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{m.title}</Typography>
                      {m.duration_in_minutes && (
                        <Typography variant="caption" color="text.secondary">
                          {m.duration_in_minutes} min
                        </Typography>
                      )}
                    </Box>
                    <School sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </Stack>
                </Paper>
              ))
              : (path.modules || []).slice(0, 10).map((uid, i) => (
                <Paper key={uid} sx={{ p: 2, borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" fontWeight={700} color="primary">{i + 1}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{uid}</Typography>
                  </Stack>
                </Paper>
              ))
            }
          </Stack>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb', position: 'sticky', top: 80 }}>
            {path.social_image_url && (
              <Box component="img" src={path.social_image_url} alt="" sx={{ width: '100%', borderRadius: '10px', mb: 2 }} />
            )}
            <Button
              fullWidth variant="contained" size="large" endIcon={<OpenInNew />}
              href={path.url} target="_blank" rel="noopener noreferrer"
              sx={{ borderRadius: '10px', fontWeight: 700, mb: 1.5 }}
            >
              Start on Microsoft Learn
            </Button>
            {path.firstModuleUrl && (
              <Button
                fullWidth variant="outlined" size="large" endIcon={<OpenInNew />}
                href={path.firstModuleUrl} target="_blank" rel="noopener noreferrer"
                sx={{ borderRadius: '10px', fontWeight: 600 }}
              >
                Start first module
              </Button>
            )}

            <Divider sx={{ my: 2 }} />

            {(path.products || []).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>PRODUCTS</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {path.products.map((p) => <Chip key={p} label={p} size="small" sx={{ backgroundColor: '#f3f4f6' }} />)}
                </Stack>
              </Box>
            )}
            {(path.roles || []).length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>ROLES</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                  {path.roles.map((r) => <Chip key={r} label={r.replace(/-/g, ' ')} size="small" sx={{ backgroundColor: '#f3f4f6', textTransform: 'capitalize' }} />)}
                </Stack>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
